import type { ReactNode } from "react";
import { useMemo } from "react";
import { Box, Grid, Icon } from "@mui/material";
import {
  campaignFormatHelper,
  getCampaignStatusText,
  getFormatDate,
  getStatusCode,
} from "@/helpers/utils";
import LearnMoreButton from "@/components/muiCustom/buttons/LearnMoreButton";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import TagChipContainer from "@/components/muiCustom/chip/TagChip";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import JoinByDate from "@/features/campaigns/components/joinCampaigns/JoinByDate";
import DirectMailCampaignJoinButton from "./DirectMailCampaignJoinButton";
import CampaignImageSlider from "./CampaignImageSlider";
import {
  DIRECT_MAIL_CAMPAIGN_TYPE,
  DIRECT_MAIL_CAMP_PROVIDER,
} from "@/features/campaigns/direct-mail/utils/directMailConstants";
import {
  formatNationalMailingWeek,
  isNationalPlusUpCampaign,
} from "@/features/campaigns/direct-mail/national-plus-up/utils/nationalPlusUpHelpers";
import {
  cardDetailSx,
  cardWrapperSx,
} from "@/features/campaigns/shared/styles/campaignCardSx";
import type { DirectMailCampaignCardData } from "./directMailCampaignCardTypes";

type RouterTo = {
  pathname: string;
  state: {
    campaignId?: string | number;
    from: string;
  };
};

const DRAFT_BORDER_STYLE = {
  border: "1px solid var(--neutral-200)",
  borderRadius: "var(--radius-container)",
};

type DirectMailCampaignCardProps = {
  campaign?: DirectMailCampaignCardData;
  isLoading?: boolean;
  participantId?: string | number;
};

type CampaignDetailProps = {
  label: string;
  value?: ReactNode;
  labelClassName?: string;
  valueClassName?: string;
};

const hasDraft = (campaign?: DirectMailCampaignCardData) =>
  campaign?.has_drafts ||
  campaign?.participant_campaign?.is_draft ||
  campaign?.is_draft;

const CampaignDetail = ({
  label,
  value,
  labelClassName = "",
  valueClassName = "",
}: CampaignDetailProps) => (
  <div className="detail">
    <div className={`title ${labelClassName}`}>{label}</div>
    <div className={`title-val ${valueClassName}`}>{value}</div>
  </div>
);

function DirectMailCampaignCard({
  campaign,
  isLoading,
  participantId,
}: DirectMailCampaignCardProps) {
  const { tags } = campaign || {};

  const isDraft = hasDraft(campaign);
  const formatter = useMemo(() => campaignFormatHelper(campaign), [campaign]);

  const isRrd = campaign?.getCampaignProvider === DIRECT_MAIL_CAMP_PROVIDER.RRD;
  const isMailSharkLimited =
    campaign?.campaign_type === DIRECT_MAIL_CAMPAIGN_TYPE.LIMITED &&
    campaign?.getCampaignProvider === DIRECT_MAIL_CAMP_PROVIDER.MAIL_SHARK;
  const isNationalPlusUp = isNationalPlusUpCampaign(campaign);
  const eligibleLocationsCount =
    campaign?.eligible_locations_count ?? campaign?.eligibleLocationsCount;
  const shouldShowCountdown = !(
    isNationalPlusUp && Number(eligibleLocationsCount) === 0
  );

  return (
    <Box
      className="h-100 position-relative fade-in"
      style={isDraft ? DRAFT_BORDER_STYLE : {}}
      sx={{ ...cardDetailSx, ...cardWrapperSx }}
    >
      <div className="card campaign-card h-100">
        <div className="card-body pr-3 pl-3 pt-3 pb-2 position-relative border-bottom">
          <Grid container alignItems="center" spacing={1}>
            <Grid size="grow">
              <CampaignDetail
                label="Campaign Name"
                value={campaign?.campaign_name}
                valueClassName="campaign-name"
              />
            </Grid>
            {isDraft && (
              <Grid size="auto">
                <StatusChip
                  label={getCampaignStatusText(5)}
                  status={getStatusCode("draft")}
                />
              </Grid>
            )}
            <Grid size={12}>
              <TagChipContainer tags={tags} />
            </Grid>
          </Grid>

          <Box my={2}>
            <CampaignImageSlider campaign={campaign} isLoading={isLoading} />
          </Box>

          {isRrd && (
            <Grid container spacing={1}>
              <Grid size={6}>
                <CampaignDetail
                  label="Eligible Locations"
                  labelClassName="fw-sb"
                  value={campaign?.eligible_locations_count ?? 0}
                  valueClassName="fw-b"
                />
              </Grid>
              {isNationalPlusUp ? (
                <Grid size={6} sx={{ textAlign: "right" }}>
                  <CampaignDetail
                    label="Mailing Week"
                    labelClassName="fw-sb"
                    value={formatNationalMailingWeek(campaign?.mailing_week)}
                    valueClassName="fw-b"
                  />
                </Grid>
              ) : null}
            </Grid>
          )}

          {isMailSharkLimited && (
            <Grid container spacing={1}>
              <Grid size={6}>
                <CampaignDetail
                  label="Join By Date"
                  value={
                    campaign?.join_by_date ? (
                      <>
                        <Icon
                          component={RpSvgIcon}
                          name="campaign_calender"
                          color="primary"
                          fontSize="small"
                        />
                        {getFormatDate(campaign.join_by_date)}
                      </>
                    ) : (
                      "N/A"
                    )
                  }
                />
              </Grid>
              <Grid size={6} sx={{ textAlign: "right" }}>
                <CampaignDetail
                  label="Promotion Date"
                  value={formatter.startDate("MMM DD, YYYY")}
                />
              </Grid>
            </Grid>
          )}
        </div>

        <div className="card-footer pt-3" style={{ minHeight: "72px" }}>
          <Grid container alignItems="center" spacing={1}>
            <Grid size={4}>
              {shouldShowCountdown ? (
                <JoinByDate
                  joinByDate={campaign?.join_by_date}
                  deadlineTime={isNationalPlusUp ? "23:59:00" : undefined}
                />
              ) : null}
            </Grid>
            <Grid size={2} sx={{ textAlign: "right" }}>
              <LearnMoreButton
                to={
                  {
                    pathname: `/dashboard/learn-more/direct-mail-campaign/${campaign?.id}`,
                    state: { campaignId: campaign?.id, from: "/dashboard" },
                  } as RouterTo
                }
                icon
                imagePath="/img/icons/bulb_pink.svg"
              />
            </Grid>
            <Grid size={6}>
              <DirectMailCampaignJoinButton
                campaign={campaign}
                participantId={participantId}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </Box>
  );
}

export default DirectMailCampaignCard;
